/**
 * The side menu for each role (M31): groups of pages with their icons. The
 * labels are the pages' names elsewhere too (tests find links by them).
 */
import {
  Activity,
  BookOpenCheck,
  Building2,
  ClipboardCheck,
  FileCheck2,
  FilePlus2,
  FolderKanban,
  Gauge,
  Globe2,
  KeyRound,
  Library,
  Landmark,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  MapPinned,
  Palette,
  Plug,
  ScrollText,
  Settings,
  ShieldCheck,
  Sigma,
  Users,
  UsersRound,
} from "lucide-react";

import type { SignedInUser } from "@/api/client";
import { currentRole } from "@/auth/session";
import { IS_DESKTOP } from "@/desktop/mode";
import { guideFor } from "@/guides/registry";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Active only on this exact address, not the pages below it. */
  exact?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

const PLATFORM: NavGroup = {
  label: "navGroups.platform",
  items: [
    { to: "/platform/states", label: "nav.states", icon: Globe2 },
    { to: "/platform/authorities", label: "nav.authorities", icon: Landmark },
    { to: "/platform/accounts", label: "nav.accounts", icon: UsersRound },
    { to: "/platform/golden", label: "nav.platformGolden", icon: ShieldCheck },
    { to: "/platform/activity", label: "nav.platformActivity", icon: Activity },
  ],
};

const OVERVIEW: NavGroup = {
  label: "navGroups.overview",
  items: [{ to: "/", label: "nav.home", icon: LayoutDashboard, exact: true }],
};

/** The menu each role sees; a platform admin also sees the platform's.
 * Everyone working for an authority sees the guide to its drawing standard
 * (M45, M47). */
export function navGroups(session: SignedInUser): NavGroup[] {
  const groups = roleGroups(session);
  const guide = guideFor(session.authority?.drawingStandard);
  if (!guide) return groups;
  const guides: NavGroup = {
    label: "navGroups.guides",
    items: [{ to: guide.to, label: guide.label, icon: Palette }],
  };
  // Before the platform's, after the authority's own.
  const at = groups.findIndex((group) => group === PLATFORM);
  return at < 0 ? [...groups, guides] : [...groups.slice(0, at), guides, ...groups.slice(at)];
}

function roleGroups(session: SignedInUser): NavGroup[] {
  if (IS_DESKTOP) {
    return [
      {
        label: "navGroups.work",
        items: [
          { to: "/pre-checks/new", label: "desktop.nav.check", icon: FilePlus2 },
          { to: "/pre-checks", label: "nav.preChecks", icon: ListChecks, exact: true },
          { to: "/licence", label: "desktop.nav.licence", icon: KeyRound },
        ],
      },
    ];
  }
  const platform = session.user.isPlatformAdmin ? [PLATFORM] : [];
  switch (currentRole(session)) {
    case "ARCHITECT":
      return [
        OVERVIEW,
        {
          label: "navGroups.work",
          items: [
            { to: "/pre-checks", label: "nav.preChecks", icon: ClipboardCheck },
            { to: "/applications", label: "nav.applications", icon: FolderKanban },
          ],
        },
        ...platform,
      ];
    case "OFFICER":
      return [
        OVERVIEW,
        {
          label: "navGroups.work",
          items: [
            { to: "/review", label: "nav.reviewQueue", icon: FileCheck2 },
            { to: "/golden", label: "nav.golden", icon: Gauge },
          ],
        },
        ...platform,
      ];
    case "ADMIN":
      return [
        OVERVIEW,
        {
          label: "navGroups.people",
          items: [
            { to: "/admin/members", label: "nav.members", icon: Users },
            { to: "/admin/activity", label: "nav.auditLog", icon: ScrollText },
          ],
        },
        {
          label: "navGroups.bylaws",
          items: [
            { to: "/admin/rule-sets", label: "nav.bylaws", icon: BookOpenCheck },
            { to: "/admin/scrutiny-parameters", label: "nav.scrutinyParameters", icon: ListChecks },
            { to: "/admin/formulas", label: "nav.formulas", icon: Sigma },
            { to: "/admin/byelaws", label: "nav.byelawRegister", icon: Library },
            { to: "/admin/zones-uses", label: "nav.zonesUses", icon: MapPinned },
            { to: "/golden", label: "nav.golden", icon: Gauge },
          ],
        },
        {
          label: "navGroups.authority",
          items: [
            { to: "/admin/authority", label: "nav.settings", icon: Settings },
            { to: "/admin/integrations", label: "nav.integrations", icon: Plug },
            { to: "/admin/licences", label: "nav.licences", icon: KeyRound },
          ],
        },
        ...platform,
      ];
    default:
      return [OVERVIEW, ...platform];
  }
}

/** The menu item an address belongs to (the longest match), and its group. */
export function activeItem(
  groups: NavGroup[],
  pathname: string,
): { group: NavGroup; item: NavItem } | null {
  let best: { group: NavGroup; item: NavItem } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const matches = item.exact
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && (best === null || item.to.length > best.item.to.length)) {
        best = { group, item };
      }
    }
  }
  return best;
}

export const BRAND_ICON = Building2;
